import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ApartmentIcon from '@mui/icons-material/Apartment';
import {
  Client,
  HypingProjectOption,
  Stage,
  STAGES,
  STAGE_COLORS,
  STAGE_LABELS,
  fetchClients,
  fetchHypingProjects,
  parseHypingProjects,
} from '../services/api';
import { maskMobileNumber, useMobileMask } from '../context/MobileMaskContext';

/** Filters mirrored from the Clients list so the map shows the same working set. */
export interface ClientMapFilters {
  search?: string;
  type?: string;
  stage?: string;
  campaign?: string;
  /** 7-day engagement day sub-filter (1-7), mirrors the list view. */
  engagementDay?: number;
  tags?: string[];
  adminMode?: boolean;
}

/** How markers are positioned: at each client's live GPS, or at the projects they hyped. */
type PlotMode = 'current' | 'project';

const SINGAPORE_CENTER: [number, number] = [1.3521, 103.8198];
// Zoom that frames the whole island — the map always opens here.
const SINGAPORE_ZOOM = 12;

// OneMap's grey basemap: keyless, Singapore-only, and low-contrast so the stage-coloured
// pins stay legible. Tiles only exist for zoom 11-19, so the map is clamped to that range.
const ONEMAP_TILE = 'https://www.onemap.gov.sg/maps/tiles/Grey/{z}/{x}/{y}.png';
const ONEMAP_ATTR =
  '<a href="https://www.onemap.gov.sg/" target="_blank" rel="noopener">OneMap</a> &copy; Singapore Land Authority';
const ONEMAP_MIN_ZOOM = 11;
const ONEMAP_MAX_ZOOM = 19;
// Fetch the full working set (the list is paginated; the map is not).
const MAP_FETCH_LIMIT = 3000;

function isValidCoordinate(lat: number | null | undefined, lng: number | null | undefined): lat is number {
  if (lat == null || lng == null) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function stageColor(stage: string): string {
  return STAGE_COLORS[stage as Stage] || '#64748b';
}

// Coloured pin, mirroring the SessionAttendeesMap marker style.
function markerIcon(color: string) {
  return L.divIcon({
    className: 'sc-client-marker',
    html: `<div style="background-color:${color};width:16px;height:16px;border-radius:50% 50% 50% 0;border:2px solid var(--color-white,#fff);box-shadow:0 2px 4px rgba(0,0,0,0.3);transform:rotate(-45deg);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

// High-contrast cluster bubble (brand orange, white bold count) so the number
// stays readable over any basemap — the default cluster colours wash out.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function clusterIcon(cluster: any) {
  const count: number = cluster.getChildCount();
  const size = count < 10 ? 34 : count < 100 ? 40 : 48;
  return L.divIcon({
    className: 'sc-cluster-icon',
    html: `<div style="background:#ed8936;color:#fff;width:${size}px;height:${size}px;`
      + `border-radius:50%;display:flex;align-items:center;justify-content:center;`
      + `font-weight:800;font-size:13px;border:3px solid #fff;`
      + `box-shadow:0 2px 6px rgba(0,0,0,0.45);">${count}</div>`,
    iconSize: [size, size],
  });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string
  ));
}

// Popup markup for an imperative (cluster-group) marker.
// Built as a string for Leaflet, so it takes the mask flag rather than the hook.
function popupHtml(p: MapPoint, masked: boolean): string {
  const color = stageColor(p.stage);
  const stageLabel = STAGE_LABELS[p.stage as Stage] || p.stage;
  const typeLabel = p.client.Type ? ` · ${p.client.Type === 'buyer' ? 'Buyer' : 'Seller'}` : '';
  const mobile = masked ? maskMobileNumber(p.client.Mobile) : (p.client.Mobile || '');
  const line3 = mobile ? `<br/>${escapeHtml(mobile)}` : '';
  const line4 = p.projectName
    ? `<br/>Hyped: ${escapeHtml(p.projectName)}`
    : (p.client.CurrentLocation ? `<br/>${escapeHtml(p.client.CurrentLocation)}` : '');
  return `<strong>${escapeHtml(p.client.Name)}</strong><br/>`
    + `<span style="color:${color}">${escapeHtml(stageLabel)}</span>${escapeHtml(typeLabel)}`
    + line3 + line4;
}

// Fan out markers that share the exact same coordinate (e.g. many clients who
// hyped the same project) so they don't stack into a single unclickable pin.
function spread(lat: number, lng: number, index: number, count: number): [number, number] {
  if (count <= 1) return [lat, lng];
  const radius = 0.0006; // ~65m
  const angle = (2 * Math.PI * index) / count;
  return [lat + radius * Math.cos(angle), lng + radius * Math.sin(angle)];
}

interface MapPoint {
  key: string;
  lat: number;
  lng: number;
  client: Client;
  stage: string;
  /** Project name when plotted in project mode; null in current-location mode. */
  projectName: string | null;
}

// Renders the points into a Leaflet marker-cluster group: nearby markers collapse
// into a numbered cluster; zooming in (or clicking a cluster) breaks them apart
// into individual pins. Managed imperatively because react-leaflet has no built-in
// cluster component.
function ClusteredMarkers({ points }: { points: MapPoint[] }) {
  const map = useMap();
  const { masked } = useMobileMask();
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const group = (L as any).markerClusterGroup({
      chunkedLoading: true,
      showCoverageOnHover: false,
      maxClusterRadius: 55,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: clusterIcon,
    }) as L.LayerGroup;
    points.forEach((p) => {
      const marker = L.marker([p.lat, p.lng], { icon: markerIcon(stageColor(p.stage)) });
      marker.bindPopup(popupHtml(p, masked));
      group.addLayer(marker);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [points, map, masked]);
  return null;
}

export default function ClientsMap({ filters }: { filters: ClientMapFilters }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<HypingProjectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<PlotMode>('current');
  const [projectFilter, setProjectFilter] = useState<string>('');

  const filterKey = JSON.stringify({
    search: filters.search || '',
    type: filters.type || '',
    stage: filters.stage || '',
    campaign: filters.campaign || '',
    engagementDay: filters.engagementDay || 0,
    tags: filters.tags || [],
    adminMode: filters.adminMode,
  });

  // Load the working set whenever the shared filters change.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchClients({
      search: filters.search || undefined,
      type: filters.type || undefined,
      stage: filters.stage || undefined,
      campaign: filters.campaign || undefined,
      engagementDay: filters.engagementDay || undefined,
      tags: filters.tags && filters.tags.length > 0 ? filters.tags : undefined,
      adminMode: filters.adminMode,
      limit: MAP_FETCH_LIMIT,
      offset: 0,
    })
      .then((res) => { if (active) setClients(res.clients); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load clients'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // filterKey captures every field we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Project coordinates (for project mode + the project filter dropdown).
  useEffect(() => {
    let active = true;
    fetchHypingProjects()
      .then((list) => { if (active) setProjects(list); })
      .catch(() => { if (active) setProjects([]); });
    return () => { active = false; };
  }, []);

  const projectCoords = useMemo(() => {
    const byId = new Map<string, HypingProjectOption>();
    const byName = new Map<string, HypingProjectOption>();
    projects.forEach((p) => {
      if (p.projectId) byId.set(p.projectId, p);
      if (p.projectName) byName.set(p.projectName.trim().toLowerCase(), p);
    });
    return { byId, byName };
  }, [projects]);

  // Only offer projects that clients have actually hyped, in the filter dropdown.
  const hypedProjectOptions = useMemo(() => {
    const seen = new Map<string, string>();
    clients.forEach((c) => parseHypingProjects(c).forEach((ref) => {
      const key = ref.id || ref.name;
      if (key && !seen.has(key)) seen.set(key, ref.name);
    }));
    return Array.from(seen.entries()).map(([key, name]) => ({ key, name }));
  }, [clients]);

  const points = useMemo<MapPoint[]>(() => {
    if (mode === 'current') {
      const filtered = projectFilter
        ? clients.filter((c) => parseHypingProjects(c).some((r) => (r.id || r.name) === projectFilter))
        : clients;
      return filtered.flatMap((c) =>
        isValidCoordinate(c.CurrentLocationLat, c.CurrentLocationLng)
          ? [{ key: `cur-${c.Id}`, lat: c.CurrentLocationLat!, lng: c.CurrentLocationLng!, client: c, stage: c.Stage, projectName: null }]
          : [],
      );
    }
    // Project mode: one marker per (client, hyped project) at the project's pin.
    const collected: Array<{ base: [number, number]; point: Omit<MapPoint, 'lat' | 'lng'> }> = [];
    clients.forEach((c) => {
      parseHypingProjects(c).forEach((ref) => {
        if (projectFilter && (ref.id || ref.name) !== projectFilter) return;
        const proj = (ref.id && projectCoords.byId.get(ref.id))
          || projectCoords.byName.get(ref.name.trim().toLowerCase());
        if (!proj || !isValidCoordinate(proj.mapLatitude, proj.mapLongitude)) return;
        collected.push({
          base: [proj.mapLatitude!, proj.mapLongitude!],
          point: { key: `proj-${c.Id}-${ref.id || ref.name}`, client: c, stage: c.Stage, projectName: ref.name },
        });
      });
    });
    // Group by coordinate so co-located clients fan out.
    const groups = new Map<string, typeof collected>();
    collected.forEach((item) => {
      const k = `${item.base[0].toFixed(5)},${item.base[1].toFixed(5)}`;
      const arr = groups.get(k) || [];
      arr.push(item);
      groups.set(k, arr);
    });
    const out: MapPoint[] = [];
    groups.forEach((items) => {
      items.forEach((item, i) => {
        const [lat, lng] = spread(item.base[0], item.base[1], i, items.length);
        out.push({ ...item.point, lat, lng });
      });
    });
    return out;
  }, [clients, mode, projectFilter, projectCoords]);

  // How many clients in the working set have no plottable coordinate in this mode.
  const missing = useMemo(() => {
    if (mode === 'current') {
      const base = projectFilter
        ? clients.filter((c) => parseHypingProjects(c).some((r) => (r.id || r.name) === projectFilter))
        : clients;
      return base.filter((c) => !isValidCoordinate(c.CurrentLocationLat, c.CurrentLocationLng)).length;
    }
    const plotted = new Set(points.map((p) => p.client.Id));
    const base = projectFilter
      ? clients.filter((c) => parseHypingProjects(c).some((r) => (r.id || r.name) === projectFilter))
      : clients.filter((c) => parseHypingProjects(c).length > 0);
    return base.filter((c) => !plotted.has(c.Id)).length;
  }, [clients, mode, projectFilter, points]);

  const stagesInView = useMemo(() => {
    const set = new Set(points.map((p) => p.stage));
    return STAGES.filter((s) => set.has(s));
  }, [points]);

  return (
    <Box>
      {/* Map controls: plot mode + project filter */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={{
            '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 },
            '& .Mui-selected': { color: '#ed8936 !important', bgcolor: '#ed893615 !important' },
          }}
        >
          <ToggleButton value="current"><MyLocationIcon fontSize="small" sx={{ mr: 0.5 }} />Current location</ToggleButton>
          <ToggleButton value="project"><ApartmentIcon fontSize="small" sx={{ mr: 0.5 }} />Hyped project</ToggleButton>
        </ToggleButtonGroup>

        {hypedProjectOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Hyping project</InputLabel>
            <Select
              label="Hyping project"
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <MenuItem value="">All projects</MenuItem>
              {hypedProjectOptions.map((p) => (
                <MenuItem key={p.key} value={p.key}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
          {points.length} plotted{missing > 0 ? ` · ${missing} without ${mode === 'current' ? 'GPS location' : 'a project pin'}` : ''}
        </Typography>
      </Box>

      {/* Stage legend */}
      {stagesInView.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          {stagesInView.map((s) => (
            <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: stageColor(s), border: '2px solid var(--color-white,#fff)', boxShadow: '0 0 0 1px var(--color-border)' }} />
              <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>{STAGE_LABELS[s]}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ position: 'relative', height: '65vh', minHeight: 420, borderRadius: 2, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.05)' }}>
            <CircularProgress />
          </Box>
        )}
        {error && !loading && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: '#ef4444' }}>{error}</Typography>
          </Box>
        )}
        {!loading && !error && points.length === 0 && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <Typography variant="body2" sx={{ color: 'var(--color-text-faint)', bgcolor: 'var(--color-surface)', px: 2, py: 1, borderRadius: 1 }}>
              {mode === 'current'
                ? 'No clients have a saved GPS location yet.'
                : 'No hyped projects with map pins to plot.'}
            </Typography>
          </Box>
        )}
        <MapContainer
          center={SINGAPORE_CENTER}
          zoom={SINGAPORE_ZOOM}
          minZoom={ONEMAP_MIN_ZOOM}
          maxZoom={ONEMAP_MAX_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          {/* Light, low-detail basemap so coloured markers/clusters read clearly.
              OneMap (SLA) needs no API key and only serves Singapore, which is all this map plots. */}
          <TileLayer
            attribution={ONEMAP_ATTR}
            url={ONEMAP_TILE}
            minZoom={ONEMAP_MIN_ZOOM}
            maxZoom={ONEMAP_MAX_ZOOM}
          />
          <ClusteredMarkers points={points} />
        </MapContainer>
      </Box>
    </Box>
  );
}
